import { voyageReportNumber } from '@felix/domain';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { browserPdfOutput, pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import type { PmsAttachment } from '../pms/types.js';
import { voyageReportPdf, voyageReportPdfFileName } from './report-pdf.js';
import type { VoyageSource } from './source.js';
import type { Voyage, VoyageReport } from './types.js';

/**
 * Sefer raporunun ekleri (ADR 0057 §3): kaydedilmiş raporun ekleri okunur; gemide taslak ya da
 * yeniden açılmış rapora yüklenir. Hata kodu döner; ileti ekranındır.
 */
export function useReportAttachments(source: VoyageSource, report: VoyageReport | null) {
  const [documents, setDocuments] = useState<PmsAttachment[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const id = report?.id ?? null;
  const reload = useCallback(async () => {
    if (!id || !source.attachments) {
      setDocuments(id ? [] : null);
      return;
    }
    try {
      setDocuments(await source.attachments(id));
    } catch {
      setDocuments([]);
    }
  }, [id, source]);
  useEffect(() => {
    setDocuments(null);
    void reload();
  }, [reload]);
  const open =
    !!report &&
    (report.status === 'draft' || report.status === 'reopened') &&
    (report.can?.edit ?? true);
  const canUpload = !!source.editor?.upload && open;
  const upload = async (file: File): Promise<string | null> => {
    if (!report || !source.editor?.upload) return 'FORBIDDEN';
    setUploading(true);
    try {
      await source.editor.upload(report.id, file);
      await reload();
      return null;
    } catch (error) {
      return errorCode(error);
    } finally {
      setUploading(false);
    }
  };
  return { documents, uploading, canUpload, upload };
}

/**
 * Raporun ortak PDF'i (ADR 0057 §7): kayıtlı içerikten üretilir (formdaki kaydedilmemiş
 * değişiklik basılmaz). `print` yazdırma penceresiyle, `preview` yazdırmadan açar.
 */
export function useReportPrint(
  source: VoyageSource,
  context: {
    report: VoyageReport | null;
    reports: readonly VoyageReport[];
    voyage: Voyage | null;
    vessel: { name: string; imo?: string | null } | null;
  },
) {
  const { t } = useTranslation();
  const [printing, setPrinting] = useState<'print' | 'preview' | null>(null);
  const { report, reports, voyage, vessel } = context;
  // Sefersiz kalkış da basılır (ADR 0058 §3): sefer satırı boş kalır
  const ready = !!report && !!vessel;
  const run = async (mode: 'print' | 'preview'): Promise<string | null> => {
    if (!report || !vessel) return null;
    setPrinting(mode);
    try {
      const brand = (await source.brand?.().catch(() => null)) ?? null;
      const tr = (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {}));
      const definition = voyageReportPdf(tr, {
        report,
        reports,
        voyage,
        vessel,
        brand,
        printedAt: new Date().toISOString(),
      });
      const number =
        report.reportNo ?? (voyage ? voyageReportNumber(voyage.voyageNo, report, reports) : '');
      const fileName = voyageReportPdfFileName(number, t(`voyage.kinds.${report.kind}`));
      const output = source.pdf ?? browserPdfOutput();
      await output.print(fileName, () => renderPdf(definition, { print: mode === 'print' }));
      return null;
    } catch (error) {
      const key = pdfErrorKey(errorCode(error));
      return key === 'restHours.pdf.error' ? t('voyage.pdf.error') : t(key);
    } finally {
      setPrinting(null);
    }
  };
  return { ready, printing, print: () => run('print'), preview: () => run('preview') };
}
